import React, { useState } from 'react';
import { X, Phone, GraduationCap, Briefcase, Monitor, Video, FileText, List, Download, Printer, Star, MapPin, Mail, Globe, Calendar, Users, Award, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Separator } from '../ui/separator';
import { trainerProfileMock } from '../../data/trainerProfileMock';

const SimplifyMyTrainingProfile = ({ onClose, trainer = trainerProfileMock }) => {
  const [activeTab, setActiveTab] = useState('contact');
  const [expandedExperience, setExpandedExperience] = useState({});

  const tabs = [
    { id: 'contact', label: 'Contact', icon: Phone, color: 'bg-green-500' },
    { id: 'education', label: 'Education', icon: GraduationCap, color: 'bg-blue-500' },
    { id: 'workExp', label: 'Work Exp', icon: Briefcase, color: 'bg-purple-500' },
    { id: 'trainingExp', label: 'Training Exp', icon: Monitor, color: 'bg-orange-500' },
    { id: 'inAction', label: 'In Action', icon: Video, color: 'bg-red-500' },
    { id: 'testimonials', label: 'Testimonials', icon: FileText, color: 'bg-gray-500' },
    { id: 'preferences', label: 'Preference', icon: List, color: 'bg-blue-600' }
  ];

  const toggleExperience = (id) => {
    setExpandedExperience(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handlePrintPDF = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;
      
      // Create a temporary div with the profile content
      const element = document.createElement('div');
      element.innerHTML = `
        <div style="font-family: Arial, sans-serif; padding: 20px; background: white; width: 800px;">
          <!-- Header -->
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px;">
            <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 50'%3E%3Crect width='200' height='50' fill='%23007acc'/%3E%3Ctext x='100' y='30' text-anchor='middle' fill='white' font-family='Arial' font-size='14' font-weight='bold'%3E${trainer.basicInfo.organization}%3C/text%3E%3C/svg%3E" alt="Logo" style="height: 40px; margin-bottom: 15px;" />
            <h1 style="margin: 10px 0; font-size: 28px; color: #333;">Profile - ${trainer.basicInfo.name}</h1>
            <div style="background: #4a5568; color: white; padding: 20px; margin: 20px 0; text-align: left; display: flex; align-items: center;">
              <div style="width: 80px; height: 80px; background: #ccc; border-radius: 50%; margin-right: 20px; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: bold;">${trainer.basicInfo.firstName.charAt(0)}</div>
              <div>
                <h2 style="margin: 0; font-size: 24px;">${trainer.basicInfo.firstName}</h2>
                <p style="margin: 5px 0; font-size: 18px;">${trainer.basicInfo.location}</p>
              </div>
            </div>
          </div>

          <!-- Contact Information -->
          <div style="margin-bottom: 30px;">
            <div style="background: #22c55e; color: white; padding: 10px; font-weight: bold; margin-bottom: 10px;">📞 Contact Information</div>
            <div style="padding: 15px; border: 1px solid #ddd;">
              <p><strong>Phone:</strong> ${trainer.contact.phone}</p>
              <p><strong>Email:</strong> ${trainer.contact.email}</p>
              <p><strong>LinkedIn:</strong> ${trainer.contact.linkedin}</p>
              <p><strong>Website:</strong> ${trainer.contact.website}</p>
              <p><strong>Address:</strong> ${trainer.contact.address.street}, ${trainer.contact.address.city}, ${trainer.contact.address.state} ${trainer.contact.address.zipCode}</p>
            </div>
          </div>

          <!-- Education -->
          <div style="margin-bottom: 30px;">
            <div style="background: #3b82f6; color: white; padding: 10px; font-weight: bold; margin-bottom: 10px;">🎓 Education</div>
            <div style="padding: 15px; border: 1px solid #ddd;">
              ${trainer.education.map(edu => `
                <div style="margin-bottom: 15px; border-left: 4px solid #3b82f6; padding-left: 15px;">
                  <h4 style="margin: 0 0 5px 0; font-size: 16px;">${edu.degree}</h4>
                  <p style="margin: 0; color: #3b82f6; font-weight: bold;">${edu.institution}</p>
                  <p style="margin: 5px 0; color: #666;">${edu.location} (${edu.startDate} - ${edu.endDate})</p>
                  <p style="margin: 5px 0;">${edu.details}</p>
                  ${edu.gpa ? `<p style="margin: 5px 0; color: #22c55e;"><strong>GPA: ${edu.gpa}</strong></p>` : ''}
                </div>
              `).join('')}
            </div>
          </div>

          <!-- Work Experience -->
          <div style="margin-bottom: 30px;">
            <div style="background: #8b5cf6; color: white; padding: 10px; font-weight: bold; margin-bottom: 10px;">💼 Work Experience Details</div>
            <div style="padding: 15px; border: 1px solid #ddd;">
              ${trainer.workExperience.map(work => `
                <div style="margin-bottom: 20px; border-left: 4px solid #8b5cf6; padding-left: 15px;">
                  <h4 style="margin: 0 0 5px 0; font-size: 16px;">${work.title}</h4>
                  <p style="margin: 0; color: #8b5cf6; font-weight: bold;">${work.company}</p>
                  <p style="margin: 5px 0; color: #666;">${work.location} (${work.startDate} - ${work.endDate})</p>
                  <p style="margin: 10px 0;">${work.summary}</p>
                  <div style="margin-top: 10px;">
                    <strong>Key Responsibilities:</strong>
                    <ul style="margin: 5px 0; padding-left: 20px;">
                      ${work.details.map(detail => `<li style="margin: 3px 0;">${detail}</li>`).join('')}
                    </ul>
                  </div>
                  ${work.achievements ? `
                    <div style="margin-top: 10px;">
                      <strong>Key Achievements:</strong>
                      <ul style="margin: 5px 0; padding-left: 20px; color: #22c55e;">
                        ${work.achievements.map(achievement => `<li style="margin: 3px 0; font-weight: bold;">${achievement}</li>`).join('')}
                      </ul>
                    </div>
                  ` : ''}
                </div>
              `).join('')}
            </div>
          </div>

          <!-- Training Experience -->
          <div style="margin-bottom: 30px;">
            <div style="background: #f97316; color: white; padding: 10px; font-weight: bold; margin-bottom: 10px;">🖥️ Training Experience</div>
            <div style="padding: 15px; border: 1px solid #ddd;">
              ${trainer.trainingExperience.map(training => `
                <div style="margin-bottom: 20px; border-left: 4px solid #f97316; padding-left: 15px;">
                  <h4 style="margin: 0 0 5px 0; font-size: 16px; color: #f97316;">${training.programName}</h4>
                  <p><strong>Target Audience:</strong> ${training.targetAudience}</p>
                  <p><strong>Duration:</strong> ${training.duration}</p>
                  <p><strong>Participants Trained:</strong> ${training.participantCount}</p>
                  <p><strong>Delivery Mode:</strong> ${training.deliveryMode}</p>
                  <p><strong>Frequency:</strong> ${training.frequency}</p>
                  <p><strong>Years Delivered:</strong> ${training.yearsDelivered}</p>
                  <p><strong>Key Topics:</strong> ${training.topics.join(', ')}</p>
                </div>
              `).join('')}
            </div>
          </div>

          <!-- Testimonials -->
          <div style="margin-bottom: 30px;">
            <div style="background: #6b7280; color: white; padding: 10px; font-weight: bold; margin-bottom: 10px;">📝 Testimonials</div>
            <div style="padding: 15px; border: 1px solid #ddd;">
              ${trainer.testimonials.map(testimonial => `
                <div style="margin-bottom: 20px; border-left: 4px solid #6b7280; padding-left: 15px; background: #f9fafb; padding: 15px;">
                  <div style="margin-bottom: 10px;">
                    <strong>${testimonial.clientName}</strong> - ${testimonial.clientTitle}<br/>
                    <span style="color: #666;">${testimonial.company}</span>
                    <span style="color: #fbbf24; margin-left: 10px;">${'★'.repeat(testimonial.rating)}</span>
                  </div>
                  <blockquote style="font-style: italic; margin: 10px 0; color: #374151;">
                    "${testimonial.testimonial}"
                  </blockquote>
                  <div style="text-align: right; color: #6b7280; font-size: 12px;">
                    Program: ${testimonial.programAttended} | ${testimonial.date}
                  </div>
                </div>
              `).join('')}
            </div>
          </div>

          <!-- Preferences -->
          <div style="margin-bottom: 30px;">
            <div style="background: #2563eb; color: white; padding: 10px; font-weight: bold; margin-bottom: 10px;">📋 Preferences & Rates</div>
            <div style="padding: 15px; border: 1px solid #ddd;">
              <div style="margin-bottom: 15px;">
                <strong>Training Methods:</strong><br/>
                ${trainer.preferences.trainingMethods.join(', ')}
              </div>
              <div style="margin-bottom: 15px;">
                <strong>Industries Served:</strong><br/>
                ${trainer.preferences.industriesServed.join(', ')}
              </div>
              <div style="margin-bottom: 15px;">
                <strong>Specializations:</strong><br/>
                ${trainer.preferences.specializations.join(', ')}
              </div>
              <div style="margin-bottom: 15px;">
                <strong>Languages:</strong><br/>
                ${trainer.preferences.languages.join(', ')}
              </div>
              <div style="margin-bottom: 15px;">
                <strong>Rates:</strong><br/>
                • Full Day Rate: ${trainer.preferences.ratesAndPackages.dayRate}<br/>
                • Half Day Rate: ${trainer.preferences.ratesAndPackages.halfDayRate}<br/>
                • Keynote Speaking: ${trainer.preferences.ratesAndPackages.keynoteSpeaking}<br/>
                • Consulting (Hourly): ${trainer.preferences.ratesAndPackages.consultingHourly}
              </div>
            </div>
          </div>
        </div>
      `;

      element.style.position = 'absolute';
      element.style.left = '-9999px';
      element.style.top = '0';
      document.body.appendChild(element);

      // Generate canvas from HTML
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      // Remove the temporary element
      document.body.removeChild(element);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      // Download the PDF
      pdf.save(`${trainer.basicInfo.name.replace(/\s+/g, '_')}_Trainer_Profile.pdf`);
      
      console.log('✅ PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Fallback to print dialog
      handlePrintPDF();
    }
  };

  const generatePrintContent = () => {
    let content = '';
    
    // Contact Section
    content += `
      <div class="profile-section">
        <div class="tab-header">Contact Information</div>
        <div class="content">
          <p><strong>Phone:</strong> ${trainer.contact.phone}</p>
          <p><strong>Email:</strong> ${trainer.contact.email}</p>
          <p><strong>LinkedIn:</strong> ${trainer.contact.linkedin}</p>
          <p><strong>Website:</strong> ${trainer.contact.website}</p>
          <p><strong>Address:</strong> ${trainer.contact.address.street}, ${trainer.contact.address.city}, ${trainer.contact.address.state} ${trainer.contact.address.zipCode}</p>
        </div>
      </div>
    `;
    
    // Education Section
    content += `
      <div class="profile-section">
        <div class="tab-header">Education</div>
        <div class="content">
          ${trainer.education.map(edu => `
            <div class="work-item">
              <h4>${edu.degree}</h4>
              <p>${edu.institution}, ${edu.location} (${edu.startDate} - ${edu.endDate})</p>
              <p>${edu.details}</p>
            </div>
          `).join('')}
        </div>
      </div>
    `;
    
    // Work Experience Section
    content += `
      <div class="profile-section">
        <div class="tab-header">Work Experience Details</div>
        <div class="content">
          ${trainer.workExperience.map(work => `
            <div class="work-item">
              <h4>${work.title}</h4>
              <p>${work.company}, ${work.location} (${work.startDate} - ${work.endDate})</p>
              <p>${work.summary}</p>
              <ul>
                ${work.details.map(detail => `<li>${detail}</li>`).join('')}
              </ul>
            </div>
          `).join('')}
        </div>
      </div>
    `;
    
    return content;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'contact':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium">{trainer.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium">{trainer.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Globe className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-sm text-gray-600">Website</p>
                    <p className="font-medium text-blue-600">{trainer.contact.website}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="font-medium">
                      {trainer.contact.address.street}<br/>
                      {trainer.contact.address.city}, {trainer.contact.address.state} {trainer.contact.address.zipCode}<br/>
                      {trainer.contact.address.country}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <Separator />
            <div>
              <h4 className="font-semibold text-lg mb-3">Social Media</h4>
              <div className="flex flex-wrap gap-3">
                <Badge variant="outline" className="py-2">LinkedIn: {trainer.contact.linkedin}</Badge>
                <Badge variant="outline" className="py-2">Twitter: {trainer.contact.socialMedia.twitter}</Badge>
                <Badge variant="outline" className="py-2">Facebook: {trainer.contact.socialMedia.facebook}</Badge>
              </div>
            </div>
          </div>
        );

      case 'education':
        return (
          <div className="space-y-6">
            {trainer.education.map((edu) => (
              <div key={edu.id} className="border-l-4 border-blue-500 pl-6 pb-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{edu.degree}</h3>
                    <p className="text-blue-600 font-medium mb-1">{edu.institution}</p>
                    <div className="flex items-center text-sm text-gray-600 space-x-4 mb-2">
                      <span className="flex items-center"><MapPin className="w-4 h-4 mr-1" />{edu.location}</span>
                      <span className="flex items-center"><Calendar className="w-4 h-4 mr-1" />{edu.startDate} - {edu.endDate}</span>
                    </div>
                    <p className="text-gray-700 mb-2">{edu.details}</p>
                    {edu.gpa && <Badge variant="secondary">GPA: {edu.gpa}</Badge>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'workExp':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">Work Experience Details</h3>
            {trainer.workExperience.map((work) => (
              <div key={work.id} className="border-l-4 border-purple-500 pl-6 pb-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg">{work.title}</h4>
                    <p className="text-purple-600 font-medium">{work.company}</p>
                    <div className="flex items-center text-sm text-gray-600 space-x-4 mb-2">
                      <span><MapPin className="w-4 h-4 inline mr-1" />{work.location}</span>
                      <span><Calendar className="w-4 h-4 inline mr-1" />{work.startDate} - {work.endDate}</span>
                    </div>
                    <p className="text-gray-700 mb-3">{work.summary}</p>
                    
                    {!expandedExperience[work.id] && (
                      <Button 
                        variant="link" 
                        className="text-blue-600 p-0 h-auto font-medium"
                        onClick={() => toggleExperience(work.id)}
                      >
                        Expand <ChevronDown className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                    
                    {expandedExperience[work.id] && (
                      <div className="mt-4 space-y-4">
                        <div>
                          <h5 className="font-medium text-gray-900 mb-2">Key Responsibilities:</h5>
                          <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                            {work.details.map((detail, idx) => (
                              <li key={idx}>{detail}</li>
                            ))}
                          </ul>
                        </div>
                        {work.achievements && (
                          <div>
                            <h5 className="font-medium text-gray-900 mb-2">Key Achievements:</h5>
                            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                              {work.achievements.map((achievement, idx) => (
                                <li key={idx} className="text-green-700 font-medium">{achievement}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <Button 
                          variant="link" 
                          className="text-blue-600 p-0 h-auto font-medium"
                          onClick={() => toggleExperience(work.id)}
                        >
                          Collapse <ChevronUp className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'trainingExp':
        return (
          <div className="space-y-6">
            <div className="grid gap-6">
              {trainer.trainingExperience.map((training) => (
                <Card key={training.id} className="border-l-4 border-orange-500">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg text-orange-700">{training.programName}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                          <div className="space-y-2">
                            <p className="text-sm"><span className="font-medium">Target Audience:</span> {training.targetAudience}</p>
                            <p className="text-sm"><span className="font-medium">Duration:</span> {training.duration}</p>
                            <p className="text-sm"><span className="font-medium">Participants Trained:</span> {training.participantCount}</p>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm"><span className="font-medium">Delivery Mode:</span> {training.deliveryMode}</p>
                            <p className="text-sm"><span className="font-medium">Frequency:</span> {training.frequency}</p>
                            <p className="text-sm"><span className="font-medium">Years Delivered:</span> {training.yearsDelivered}</p>
                          </div>
                        </div>
                        <div className="mt-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Key Topics Covered:</p>
                          <div className="flex flex-wrap gap-2">
                            {training.topics.map((topic, idx) => (
                              <Badge key={idx} variant="secondary" className="bg-orange-100 text-orange-700">
                                {topic}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'inAction':
        return (
          <div className="space-y-8">
            {/* Videos Section */}
            <div>
              <h4 className="text-lg font-semibold text-red-600 mb-4 flex items-center">
                <Video className="w-5 h-5 mr-2" />
                Training Videos & Demonstrations
              </h4>
              <div className="grid gap-6">
                {trainer.inAction.videos.map((video) => (
                  <Card key={video.id}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row gap-6">
                        <div className="md:w-1/3">
                          <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                            <iframe 
                              src={video.url}
                              title={video.title}
                              className="w-full h-full rounded-lg"
                              allowFullScreen
                            />
                          </div>
                        </div>
                        <div className="md:w-2/3">
                          <h5 className="font-semibold text-lg mb-2">{video.title}</h5>
                          <p className="text-gray-700 mb-3">{video.description}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <span>Duration: {video.duration}</span>
                            <span>Views: {video.views}</span>
                            <span>Published: {video.date}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Presentations Section */}
            <div>
              <h4 className="text-lg font-semibold text-red-600 mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Conference Presentations & Keynotes
              </h4>
              <div className="space-y-4">
                {trainer.inAction.presentations.map((presentation) => (
                  <div key={presentation.id} className="border-l-4 border-red-500 pl-6 pb-4">
                    <h5 className="font-semibold text-lg">{presentation.title}</h5>
                    <p className="text-red-600 font-medium">{presentation.event}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                      <span><MapPin className="w-4 h-4 inline mr-1" />{presentation.location}</span>
                      <span><Calendar className="w-4 h-4 inline mr-1" />{presentation.date}</span>
                      <span><Users className="w-4 h-4 inline mr-1" />Audience: {presentation.audienceSize}</span>
                    </div>
                    <p className="text-gray-700 mt-2">{presentation.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Media Features Section */}
            <div>
              <h4 className="text-lg font-semibold text-red-600 mb-4 flex items-center">
                <Award className="w-5 h-5 mr-2" />
                Media Features & Publications
              </h4>
              <div className="space-y-4">
                {trainer.inAction.mediaFeatures.map((feature) => (
                  <div key={feature.id} className="border-l-4 border-red-500 pl-6">
                    <h5 className="font-semibold">{feature.title}</h5>
                    <p className="text-red-600 font-medium">{feature.publication}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                      <span><Calendar className="w-4 h-4 inline mr-1" />{feature.date}</span>
                      <Badge variant="outline">{feature.type}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'testimonials':
        return (
          <div className="space-y-6">
            {trainer.testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-l-4 border-gray-500">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="font-semibold text-gray-600">
                          {testimonial.clientName.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold">{testimonial.clientName}</h4>
                        <p className="text-gray-600 text-sm">{testimonial.clientTitle}</p>
                        <p className="text-gray-500 text-sm">{testimonial.company}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                  <blockquote className="text-gray-700 italic mb-4">
                    "{testimonial.testimonial}"
                  </blockquote>
                  <div className="flex justify-between items-center text-sm text-gray-500">
                    <span>Program: {testimonial.programAttended}</span>
                    <span>{testimonial.date}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-8">
            <div>
              <h4 className="text-lg font-semibold text-blue-600 mb-4">Training Methods & Approaches</h4>
              <div className="flex flex-wrap gap-2">
                {trainer.preferences.trainingMethods.map((method, idx) => (
                  <Badge key={idx} variant="outline" className="py-2">
                    {method}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-blue-600 mb-4">Industries Served</h4>
              <div className="flex flex-wrap gap-2">
                {trainer.preferences.industriesServed.map((industry, idx) => (
                  <Badge key={idx} variant="secondary" className="py-2">
                    {industry}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-blue-600 mb-4">Areas of Specialization</h4>
              <div className="flex flex-wrap gap-2">
                {trainer.preferences.specializations.map((spec, idx) => (
                  <Badge key={idx} className="py-2 bg-blue-100 text-blue-700 border-blue-200">
                    {spec}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-blue-600 mb-4">Delivery Modes</h4>
              <div className="flex flex-wrap gap-2">
                {trainer.preferences.deliveryModes.map((mode, idx) => (
                  <Badge key={idx} variant="outline" className="py-2">
                    {mode}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-semibold text-blue-600 mb-4">Languages</h4>
                <div className="space-y-2">
                  {trainer.preferences.languages.map((language, idx) => (
                    <p key={idx} className="text-gray-700">{language}</p>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-blue-600 mb-4">Availability</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Full Time:</span>
                    <Badge variant={trainer.preferences.availability.fullTime ? "default" : "secondary"}>
                      {trainer.preferences.availability.fullTime ? "Available" : "Not Available"}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Part Time:</span>
                    <Badge variant={trainer.preferences.availability.partTime ? "default" : "secondary"}>
                      {trainer.preferences.availability.partTime ? "Available" : "Not Available"}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Freelance:</span>
                    <Badge variant={trainer.preferences.availability.freelance ? "default" : "secondary"}>
                      {trainer.preferences.availability.freelance ? "Available" : "Not Available"}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Consulting:</span>
                    <Badge variant={trainer.preferences.availability.consulting ? "default" : "secondary"}>
                      {trainer.preferences.availability.consulting ? "Available" : "Not Available"}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-blue-600 mb-4">Rates & Packages</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="font-medium">Full Day Rate:</span>
                    <span className="text-green-600 font-semibold">{trainer.preferences.ratesAndPackages.dayRate}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="font-medium">Half Day Rate:</span>
                    <span className="text-green-600 font-semibold">{trainer.preferences.ratesAndPackages.halfDayRate}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="font-medium">Keynote Speaking:</span>
                    <span className="text-green-600 font-semibold">{trainer.preferences.ratesAndPackages.keynoteSpeaking}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="font-medium">Consulting (Hourly):</span>
                    <span className="text-green-600 font-semibold">{trainer.preferences.ratesAndPackages.consultingHourly}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="font-medium">Custom Programs:</span>
                    <span className="text-blue-600 font-semibold">{trainer.preferences.ratesAndPackages.customPrograms}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gray-100 px-6 py-4 border-b flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 40'%3E%3Crect width='100' height='40' fill='%23007acc'/%3E%3Ctext x='50' y='25' text-anchor='middle' fill='white' font-family='Arial' font-size='12' font-weight='bold'%3E{trainer.basicInfo.organization}%3C/text%3E%3C/svg%3E"
              alt="Organization Logo" 
              className="h-10"
            />
            <h1 className="text-xl font-bold">Profile - {trainer.basicInfo.name}</h1>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrintPDF}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Profile Summary */}
        <div className="px-6 py-6 bg-gradient-to-r from-gray-700 to-gray-800 text-white flex items-center space-x-6">
          <Avatar className="w-24 h-24 border-4 border-white">
            <AvatarImage src={trainer.basicInfo.profileImage} alt={trainer.basicInfo.name} />
            <AvatarFallback className="text-2xl font-bold">
              {trainer.basicInfo.name.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>
          <div>
            <h2 className="text-3xl font-bold">{trainer.basicInfo.firstName}</h2>
            <p className="text-xl text-gray-200">{trainer.basicInfo.location}</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex overflow-x-auto bg-white border-b">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center px-6 py-4 min-w-max transition-colors ${
                  activeTab === tab.id
                    ? `${tab.color} text-white`
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-6 h-6 mb-1" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderTabContent()}
        </div>

        {/* Footer */}
        <div className="bg-gray-100 px-6 py-3 border-t flex justify-end">
          <Button onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SimplifyMyTrainingProfile;